/**
 * A table's rows in the order they are drawn: the (held) key order, grouped, with a header
 * item per group. Pure, so selection ranges can be checked against exactly what is on screen.
 */
export type TableItem<T> = { kind: 'group'; key: string; rows: T[] } | { kind: 'row'; key: string; row: T; index: number };

export interface TableItemsOptions<T> {
  rowKey: (row: T) => string;
  groupBy?: (row: T) => string;
  groupSort?: (a: string, b: string) => number;
  /** Emit a header item per group (the table renders one). */
  headers?: boolean;
}

/** Groups appear in first-seen order (or `groupSort`); rows keep `order` inside each group. */
export function buildTableItems<T>(order: readonly string[], byKey: ReadonlyMap<string, T>, o: TableItemsOptions<T>): TableItem<T>[] {
  const list: T[] = [];
  for (const k of order) {
    const r = byKey.get(k);
    if (r !== undefined) list.push(r);
  }
  if (!o.groupBy) return list.map((row, index) => ({ kind: 'row', key: o.rowKey(row), row, index }));
  const groupOrder: string[] = [];
  const groups = new Map<string, T[]>();
  for (const r of list) {
    const g = o.groupBy(r);
    let arr = groups.get(g);
    if (!arr) {
      groups.set(g, (arr = []));
      groupOrder.push(g);
    }
    arr.push(r);
  }
  if (o.groupSort) groupOrder.sort(o.groupSort);
  const out: TableItem<T>[] = [];
  let index = 0;
  for (const g of groupOrder) {
    const rs = groups.get(g)!;
    if (o.headers) out.push({ kind: 'group', key: `group:${g}`, rows: rs });
    for (const row of rs) out.push({ kind: 'row', key: o.rowKey(row), row, index: index++ });
  }
  return out;
}
